import PropTypes from 'prop-types';
import React from 'react';

import * as customPropTypes from '@/custom-prop-types';
import { Breadcrumb } from '@/design-system/atoms/Breadcrumb';
import { Button } from '@/design-system/atoms/Button';
import { ContentContainer } from '@/design-system/atoms/ContentContainer';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { Text } from '@/design-system/atoms/Text';
import { Theme } from '@/design-system/atoms/Theme';
import { Title } from '@/design-system/atoms/Title';
import HeroZipForm from '@/design-system/molecules/HeroZipForm';

import styles from './HeroSecondary.module.scss';

/**
 The Hero Secondary Component purpose is to introduce the user to the page, brand, and/or site. Hero banner content should be concise and easy to digest and drive the user further down the page or into a key section of the site.
 */

const HeroSecondary = ({
    testId,
    theme = Theme.NAME.CONDITIONS,
    breadcrumbs,
    grid,
    title,
    body,
    ctas,
    enableLocationFieldForSearch,
    zipCityPlaceHolder,
    zipCtaAriaLabel,
    zipCtaHref,
    zipCtaLabel,
    ...props
}) => {
    return (
        <section data-testid={testId} className={styles['hero-secondary']} {...props}>
            <Theme name={theme}>
                <ContentContainer>
                    <div className={styles['hero-secondary__spacing-wrapper']}>
                        <Grid>
                            {breadcrumbs && (
                                <GridColumn
                                    className={styles['hero-secondary__breadcrumb']}
                                    colSpan={{ sm: 4, md: 6, lg: 12 }}
                                    colStart={{ sm: 1, lg: 1 }}
                                >
                                    <Breadcrumb
                                        items={breadcrumbs}
                                        analytics={{
                                            componentTitle: title,
                                            componentName: enableLocationFieldForSearch
                                                ? 'Hero Zip Form'
                                                : 'Hero Secondary',
                                        }}
                                    />
                                </GridColumn>
                            )}
                            <GridColumn
                                className={styles['hero-secondary__primary']}
                                colSpan={{ sm: 4, md: 6, lg: 6 }}
                                colStart={{ sm: 1, lg: 1 }}
                            >
                                <div className={styles['hero-secondary__content']}>
                                    <div className={styles['hero-secondary__title']}>
                                        <Title
                                            tag="h1"
                                            variant={Title.VARIANT.H2}
                                            content={title}
                                            lineClamp={{ sm: 3, lg: 3 }}
                                            allowInlineHTML
                                        />
                                    </div>
                                </div>
                            </GridColumn>
                            <GridColumn
                                className={styles['hero-secondary__secondary']}
                                colSpan={{ sm: 4, md: 6, lg: 6 }}
                                colStart={{ sm: 1, lg: 7 }}
                            >
                                <div className={styles['hero-secondary__content']}>
                                    {body && (
                                        <div className={styles['hero-secondary__body']}>
                                            <Text
                                                variant={Text.VARIANT.H5}
                                                content={body}
                                                lineClamp={{ sm: 12, lg: 8 }}
                                                allowInlineHTML
                                            />
                                        </div>
                                    )}
                                    {enableLocationFieldForSearch ? (
                                        <HeroZipForm
                                            zipCityPlaceHolder={zipCityPlaceHolder}
                                            zipCtaAriaLabel={zipCtaAriaLabel}
                                            zipCtaHref={zipCtaHref}
                                            zipCtaLabel={zipCtaLabel}
                                            title={title}
                                        />
                                    ) : Array.isArray(ctas) && ctas.length > 0 ? (
                                        <div className={styles['hero-secondary__buttons']}>
                                            {ctas.map((link, index) => {
                                                return (
                                                    link?.href && (
                                                        <div
                                                            className={
                                                                styles['hero-secondary__button']
                                                            }
                                                            key={index}
                                                        >
                                                            <Button
                                                                {...link}
                                                                href={link.href}
                                                                target={link.target}
                                                                csr={link.csr}
                                                                buttonStyle={
                                                                    index === 0
                                                                        ? Button.STYLE.PRIMARY
                                                                        : Button.STYLE.SECONDARY
                                                                }
                                                                label={link.label}
                                                                ariaLabel={link.ariaLabel}
                                                            />
                                                        </div>
                                                    )
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            </GridColumn>
                        </Grid>
                    </div>
                </ContentContainer>
            </Theme>
        </section>
    );
};

HeroSecondary.propTypes = {
    /** Breadcrumbs */
    breadcrumbs: PropTypes.array,
    /** Title */
    title: PropTypes.string.isRequired,
    /** Body Text */
    body: PropTypes.string,
    /** Links */
    ctas: PropTypes.arrayOf(customPropTypes.linkPropType),
    /** Theme */
    theme: PropTypes.oneOf(Object.values(Theme.NAME)),
    /** Test ID */
    testId: PropTypes.string,
};

export default HeroSecondary;
